export interface CellAttachment {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly mimeType: string;
  readonly size: number; // Size in bytes
  readonly thumbnail?: string | undefined;
  readonly width?: number | undefined;
  readonly height?: number | undefined;
  readonly uploadedAt?: string | undefined;
}

/**
 * Normalizes any cell value into a clean CellAttachment array.
 * Tolerates:
 * 1. CellAttachment[]
 * 2. JSON string of CellAttachment[] or single object
 * 3. Single CellAttachment object
 * 4. Single URL string (image or PDF)
 * 5. Comma/newline separated URL strings
 */
export function normalizeAttachments(raw: unknown): CellAttachment[] {
  if (!raw) return [];

  // Case 1: Already an array
  if (Array.isArray(raw)) {
    return raw
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .map(mapToCellAttachment)
      .filter((att): att is CellAttachment => Boolean(att.url));
  }

  // Case 2: Object
  if (typeof raw === 'object') {
    const att = mapToCellAttachment(raw as Record<string, unknown>);
    return att.url ? [att] : [];
  }

  // Case 3: String
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    // Attempt JSON parse
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        return normalizeAttachments(parsed);
      } catch {
        // Fall through to plain URL parser
      }
    }

    // Split comma or newline separated URLs without breaking data URIs
    const parts = splitUrls(trimmed);
    return parts.map((url, idx) => ({
      id: `url-att-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      name: extractFilenameFromUrl(url),
      url,
      mimeType: inferMimeTypeFromUrl(url),
      size: 0,
    }));
  }

  return [];
}

function splitUrls(input: string): string[] {
  const items: string[] = [];
  const lines = input.includes('\n') ? input.split(/\r?\n/) : [input];
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    let current = '';
    let inDataUri = false;
    let dataUriSawFirstComma = false;

    let i = 0;
    while (i < trimmedLine.length) {
      if (!inDataUri && trimmedLine.slice(i, i + 5).toLowerCase() === 'data:') {
        inDataUri = true;
        dataUriSawFirstComma = false;
        current += trimmedLine.slice(i, i + 5);
        i += 5;
        continue;
      }

      if (inDataUri && !dataUriSawFirstComma) {
        if (trimmedLine[i] === ',') {
          dataUriSawFirstComma = true;
          current += ',';
          i++;
          continue;
        }
      }

      if (trimmedLine[i] === ',') {
        if (inDataUri) {
          inDataUri = false;
          dataUriSawFirstComma = false;
        }
        const part = current.trim();
        if (part) items.push(part);
        current = '';
        i++;
        continue;
      }

      current += trimmedLine[i];
      i++;
    }

    const lastPart = current.trim();
    if (lastPart) items.push(lastPart);
  }

  return items;
}

export function inferMimeTypeFromUrl(url: string): string {
  if (url.startsWith('data:')) {
    const match = /^data:([^;,]+)/i.exec(url);
    if (match?.[1]) return match[1].toLowerCase();
  }
  const clean = url.split('?')[0]?.toLowerCase() ?? '';
  if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg';
  if (clean.endsWith('.png')) return 'image/png';
  if (clean.endsWith('.webp')) return 'image/webp';
  if (clean.endsWith('.gif')) return 'image/gif';
  if (clean.endsWith('.svg')) return 'image/svg+xml';
  if (clean.endsWith('.pdf')) return 'application/pdf';
  if (clean.endsWith('.docx') || clean.endsWith('.doc')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (clean.endsWith('.xlsx') || clean.endsWith('.xls')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (clean.endsWith('.csv')) return 'text/csv';
  return 'application/octet-stream';
}

export function isImageAttachment(att: CellAttachment): boolean {
  return att.mimeType.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(att.url);
}

export function isPdfAttachment(att: CellAttachment): boolean {
  return att.mimeType === 'application/pdf' || /\.pdf(\?.*)?$/i.test(att.url);
}

export function extractFilenameFromUrl(url: string): string {
  if (!url || typeof url !== 'string') return 'attachment';
  if (url.startsWith('data:')) {
    const match = /^data:([^;,]+)/i.exec(url);
    const subtype = match?.[1]?.split('/')[1] || 'attachment';
    return `attachment.${subtype}`;
  }
  try {
    const pathname = new URL(url, 'https://placeholder.test').pathname;
    const segments = pathname.split('/');
    const last = segments[segments.length - 1];
    if (!last || last.length === 0) return 'attachment';
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  } catch {
    return 'attachment';
  }
}

export function formatFileSize(bytes: number): string {
  if (typeof bytes !== 'number' || Number.isNaN(bytes) || !Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function mapToCellAttachment(item: Record<string, unknown>): CellAttachment {
  const url = typeof item.url === 'string' ? item.url : '';
  const name = typeof item.name === 'string' ? item.name : extractFilenameFromUrl(url);
  const mimeType = typeof item.mimeType === 'string' ? item.mimeType : inferMimeTypeFromUrl(url);
  const size = typeof item.size === 'number' ? item.size : 0;
  const thumbnail = typeof item.thumbnail === 'string' ? item.thumbnail : undefined;
  const id = typeof item.id === 'string' ? item.id : `att-${Math.random().toString(36).slice(2, 9)}`;

  return { id, name, url, mimeType, size, ...(thumbnail ? { thumbnail } : {}) };
}
